import { Children, useRef, useState, type ReactNode } from 'react';
import { Icon } from './Icon.js';
import { iconChevron, iconChevronLeft, iconPhoto } from './generated/icons.js';

export interface CarouselProps {
  label: string;
  children: ReactNode;
  previousLabel?: string;
  nextLabel?: string;
  /** The words of the position chip over the pictures, for example `1 of 8`. Without it there is no chip. */
  countLabel?: (position: number, total: number) => string;
}

/**
 * A multi-browse carousel on scroll snap. Swiping is the platform's; the two buttons and the
 * arrow keys do the same for people who cannot swipe.
 */
export function Carousel({
  label,
  children,
  previousLabel = 'Previous',
  nextLabel = 'Next',
  countLabel,
}: CarouselProps) {
  const track = useRef<HTMLDivElement>(null);
  const total = Children.count(children);
  const [position, setPosition] = useState(1);
  const move = (direction: 1 | -1) => {
    const el = track.current;
    if (el === null) return;
    el.scrollBy({ left: direction * el.clientWidth * 0.8, behavior: 'smooth' });
  };
  return (
    <section
      className="k-carousel"
      role="region"
      aria-roledescription="carousel"
      aria-label={label}
    >
      <div
        ref={track}
        className="k-carousel__track"
        tabIndex={0}
        onScroll={(event) => {
          const el = event.currentTarget;
          const first = el.firstElementChild as HTMLElement | null;
          if (first === null) return;
          const step = first.getBoundingClientRect().width + 8;
          setPosition(Math.min(total, Math.max(1, Math.round(el.scrollLeft / step) + 1)));
        }}
        onKeyDown={(event) => {
          if (event.key === 'ArrowRight') {
            event.preventDefault();
            move(1);
          } else if (event.key === 'ArrowLeft') {
            event.preventDefault();
            move(-1);
          }
        }}
      >
        {children}
      </div>
      {countLabel === undefined || total < 2 ? null : (
        <span className="k-carousel__count k-t-label-sm">
          <Icon icon={iconPhoto} size={16} />
          {countLabel(position, total)}
        </span>
      )}
      <div className="k-carousel__nav">
        <button
          type="button"
          className="k-iconbtn k-iconbtn--tonal k-layer"
          aria-label={previousLabel}
          onClick={() => move(-1)}
        >
          <Icon icon={iconChevronLeft} />
        </button>
        <button
          type="button"
          className="k-iconbtn k-iconbtn--tonal k-layer"
          aria-label={nextLabel}
          onClick={() => move(1)}
        >
          <Icon icon={iconChevron} />
        </button>
      </div>
    </section>
  );
}

export function CarouselItem({ children }: { children: ReactNode }) {
  return <div className="k-carousel__item">{children}</div>;
}
